import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import {
  ApolloCache,
  DefaultContext,
  MutationTuple,
  OperationVariables,
  useMutation,
} from "@apollo/client";
import { logEvent } from "../../../helpers/analytics";
import {
  CompanyInformationFormData,
  ContactDetailsFormData,
  CommunityInformationFormData,
  CommunitySalesConversationFormData,
  SalesConversationPreferenceFormData,
} from "../schemas/schema";
import { gql } from "../../../__generated__";
import {
  CreateCommunityManagementSalesConversationTaskMutation,
  CommunityManagementSalesConversationRequestDto,
  Exact,
} from "../../../__generated__/graphql";

export const CREATE_COMMUNITY_SALES_CONVERSATION = gql(`
  mutation CreateCommunityManagementSalesConversationTask($communityManagementSalesConversationRequestDto: CommunityManagementSalesConversationRequestDto!) {
    createCommunityManagementSalesConversationTask(communityManagementSalesConversationRequestDto: $communityManagementSalesConversationRequestDto)
  }
`);

interface CommunitySalesConversationContextType {
  currentStep: number;
  isLoading: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  submitForm: MutationTuple<
    CreateCommunityManagementSalesConversationTaskMutation,
    Exact<{ communityManagementSalesConversationRequestDto: CommunityManagementSalesConversationRequestDto }>,
    DefaultContext,
    ApolloCache<any>
  >[0];
  resetForm: () => void;
  updateCompanyInfo: (data: CompanyInformationFormData) => void;
  updateContactDetails: (data: ContactDetailsFormData) => void;
  updateCommunityInfo: (data: CommunityInformationFormData) => void;
  updateSalesConversationPreference: (data: SalesConversationPreferenceFormData) => void;
  getAllFormData: () => Partial<CommunitySalesConversationFormData>;
  getCompanyInfo: () => Partial<CompanyInformationFormData>;
  getContactDetails: () => Partial<ContactDetailsFormData>;
  getCommunityInfo: () => Partial<CommunityInformationFormData>;
  getSalesConversationPreference: () => Partial<SalesConversationPreferenceFormData>;
}

const CommunitySalesConversationContext =
  createContext<CommunitySalesConversationContextType | undefined>(undefined);

export const useCommunitySalesConversation = (): CommunitySalesConversationContextType => {
  const context = useContext(CommunitySalesConversationContext);
  if (!context) {
    throw new Error(
      "useCommunitySalesConversation must be used within a CommunitySalesConversationProvider"
    );
  }
  return context;
};

export const CommunitySalesConversationProvider = ({
  children,
}: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<
    Partial<CommunitySalesConversationFormData>
  >({});

  const [createCommunitySalesConversationTask, { loading }] = useMutation(
    CREATE_COMMUNITY_SALES_CONVERSATION
  );

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setCurrentStep(step);
      logEvent("community_sales_conversation_step_changed", { step });
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
      logEvent("community_sales_conversation_next_step", { step: currentStep + 1 });
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      logEvent("community_sales_conversation_previous_step", {
        step: currentStep - 1,
      });
    }
  }, [currentStep]);

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFormData({});
    logEvent("community_sales_conversation_reset");
  }, []);

  const updateCompanyInfo = useCallback((data: CompanyInformationFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_sales_conversation_company_info_updated");
  }, []);

  const updateContactDetails = useCallback((data: ContactDetailsFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_sales_conversation_contact_details_updated");
  }, []);

  const updateCommunityInfo = useCallback(
    (data: CommunityInformationFormData) => {
      setFormData((prev) => ({ ...prev, ...data }));
      logEvent("community_sales_conversation_community_info_updated");
    },
    []
  );

  const updateSalesConversationPreference = useCallback((data: SalesConversationPreferenceFormData) => {
    setFormData((prev) => ({ ...prev, ...data }));
    logEvent("community_sales_conversation_preference_updated");
  }, []);

  const getAllFormData = useCallback(() => formData, [formData]);

  const getCompanyInfo = useCallback((): Partial<CompanyInformationFormData> => {
    const { organization, roleInOrganization } = formData;
    return { organization, roleInOrganization };
  }, [formData]);

  const getContactDetails = useCallback((): Partial<ContactDetailsFormData> => {
    const { firstName, lastName, email, phone } = formData;
    return { firstName, lastName, email, phone };
  }, [formData]);

  const getCommunityInfo = useCallback(
    (): Partial<CommunityInformationFormData> => {
      const {
        typeOfCommunity,
        communitySize,
        communityTalent,
        majorChallenges,
        digitalToolsUsed,
      } = formData as CommunitySalesConversationFormData;
      return {
        typeOfCommunity,
        communitySize,
        communityTalent,
        majorChallenges,
        digitalToolsUsed,
      };
    },
    [formData]
  );

  const getSalesConversationPreference = useCallback(
    (): Partial<SalesConversationPreferenceFormData> => {
      const { preferredSalesConversationDate, preferredSalesConversationTime } = formData as CommunitySalesConversationFormData;
      return { preferredSalesConversationDate, preferredSalesConversationTime };
    },
    [formData]
  );



  const contextValue: CommunitySalesConversationContextType = useMemo(
    () => ({
      currentStep,
      isLoading: loading,
      goToStep,
      nextStep,
      previousStep,
      submitForm: createCommunitySalesConversationTask,
      resetForm,
      updateCompanyInfo,
      updateContactDetails,
      updateCommunityInfo,
      updateSalesConversationPreference,
      getAllFormData,
      getCompanyInfo,
      getContactDetails,
      getCommunityInfo,
      getSalesConversationPreference,
    }),
    [
      currentStep,
      loading,
      goToStep,
      nextStep,
      previousStep,
      createCommunitySalesConversationTask,
      resetForm,
      updateCompanyInfo,
      updateContactDetails,
      updateCommunityInfo,
      updateSalesConversationPreference,
      getAllFormData,
      getCompanyInfo,
      getContactDetails,
      getCommunityInfo,
      getSalesConversationPreference,
    ]
  );

  return (
    <CommunitySalesConversationContext.Provider value={contextValue}>
      {children}
    </CommunitySalesConversationContext.Provider>
  );
};
